import React, { useEffect, useRef, useState } from 'react';
import { MenuCaret } from './MenuCaret';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { useSiteWaiting } from '../approvals/ApprovalsReview';
import { manageUrl } from './manageAreas';
import { pathAfterSiteSwitch } from './siteSwitchPath';
import { showInkToast } from './InkToast';
import { siteHue, SiteSummary, useTinycmsSites } from './sites';

/** The site's icon, or its initial on a tile tinted for the site. */
export function SiteTile({
  site,
  size,
  plain = false,
}: {
  site: SiteSummary;
  size: number;
  /** All sites' caption: the favicon at 80% on the card colour, or the letter; no site colour. */
  plain?: boolean;
}) {
  const radius = size >= 30 ? 8 : 6;
  if (plain) {
    return (
      <span
        className="st-tile st-tile-plain"
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          borderRadius: 6,
          backgroundImage: site.iconUrl ? `url("${site.iconUrl.replace(/"/g, '%22')}")` : undefined,
        }}
      >
        {site.iconUrl ? null : (site.name.trim()[0] || '?').toUpperCase()}
      </span>
    );
  }
  if (site.iconUrl) {
    return (
      <img
        className="st-tile st-tile-img"
        src={site.iconUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: radius }}
      />
    );
  }
  const hue = siteHue(site.accountId);
  return (
    <span
      className="st-tile"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        fontSize: size >= 30 ? 12 : 10,
        background: `oklch(0.93 0.05 ${hue})`,
        color: `oklch(0.42 0.12 ${hue})`,
      }}
    >
      {(site.name.trim()[0] || '?').toUpperCase()}
    </span>
  );
}

/** The switcher's count badge, said in words: "3 to approve". */
export const toApprove = (n: number) => `${n} to approve`;

/**
 * The header's site menu ("tinycms UX optimization review v6.zip"): every
 * site the user belongs to, with its address and what's waiting on them, then
 * All sites (where New site is). Picking a site switches the whole app to it.
 */
export function SiteSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAccount, selectAccountById } = useAccount();
  const waitingOf = useSiteWaiting();
  const { sites, loading, error, refetch } = useTinycmsSites();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // Escape closes the menu and gives focus back to the button; scrolling the
  // page or resizing closes it too (UX review round 11). A scroll inside the
  // menu's own list doesn't.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      button.current?.focus();
    };
    const onScroll = (e: Event) => {
      if (menu.current && e.target instanceof Node && menu.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const current = sites.find((s) => s.accountId === selectedAccount?.id);
  // Until the site is known, a placeholder — never "Your site" (QA round 7).
  const label = current?.name || selectedAccount?.name || null;

  const toggle = () => {
    if (!open) void refetch();
    setOpen((v) => !v);
  };

  // Same screen and tab on the other site (Prototype v14); an open item goes
  // back to its list, and from the Sites screen it's Today.
  const pick = (site: SiteSummary) => {
    setOpen(false);
    if (site.accountId === selectedAccount?.id) return;
    navigate(pathAfterSiteSwitch(location.pathname, location.search));
    selectAccountById(site.accountId);
    showInkToast(`Switched to ${site.name}.`);
  };

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <span className="st">
      <button
        ref={button}
        type="button"
        className="st-btn"
        data-open={open ? 'true' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        {label ? (
          <span className="st-btn-name">{label}</span>
        ) : (
          <span className="st-btn-name st-btn-loading" aria-label="Loading your site" />
        )}
        <MenuCaret className="st-caret" />
      </button>
      {open ? (
        <>
          <div className="st-scrim" onClick={() => setOpen(false)} />
          <div className="st-menu" role="menu" ref={menu}>
            <div className="st-menu-label">SITES</div>
            {!sites.length ? (
              error && !loading ? (
                <button type="button" className="st-link" onClick={() => void refetch()}>
                  Your sites didn’t load. Try again
                </button>
              ) : (
                <div className="st-menu-wait" role="status">
                  Loading your sites…
                </div>
              )
            ) : null}
            {sites.map((site) => {
              const here = site.accountId === selectedAccount?.id;
              const waiting = waitingOf(site);
              return (
                <button
                  key={site.accountId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={here}
                  className="st-row"
                  onClick={() => pick(site)}
                >
                  <SiteTile site={site} size={30} />
                  <span className="st-row-text">
                    <span className="st-row-name">{site.name}</span>
                    <span className="st-row-host">{site.host || 'No address yet'}</span>
                  </span>
                  {waiting > 0 ? (
                    <span className="st-need" title={toApprove(waiting)}>
                      <span aria-hidden="true">{waiting}</span>
                      <span className="sr-only">{toApprove(waiting)}</span>
                    </span>
                  ) : null}
                  <span className="st-check" aria-hidden="true">
                    {here ? '✓' : ''}
                  </span>
                </button>
              );
            })}
            <div className="st-rule" />
            {label ? (
              <button
                type="button"
                className="st-link"
                role="menuitem"
                onClick={() => go(manageUrl('site'))}
              >
                Manage {label}
              </button>
            ) : null}
            <button type="button" className="st-link" role="menuitem" onClick={() => go('/sites')}>
              All sites
            </button>
          </div>
        </>
      ) : null}
    </span>
  );
}
